import React from 'react';
import styled from '@emotion/styled';
import { Table, TableProps } from '@applyboard/crystal-ui';

export type ColumnWidthConfig = {
  [columnKey: string]: string | number;
};

export type StyledTableProps<T> = TableProps<T> & {
  columnWidths?: ColumnWidthConfig;
};

export function StyledTable<T>(props: StyledTableProps<T>) {
  const { columnWidths, ...tableProps } = props;

  // Create CSS based on column accessorKey mapping
  const generateColumnStyles = () => {
    if (!columnWidths) return '';

    return Object.entries(columnWidths)
      .map(([accessorKey, width]) => {
        const columnIndex = props.columns.findIndex((col) => col.accessorKey === accessorKey);
        if (columnIndex === -1) return '';

        const widthValue = typeof width === 'number' ? `${width}px` : width;

        return `
          /* Target column ${columnIndex + 1} (${accessorKey}) */
          & table > thead > tr > th:nth-of-type(${columnIndex + 1}) {
            width: ${widthValue};
            min-width: ${widthValue};
            max-width: ${widthValue};
          }

          & table > tbody > tr > td:nth-of-type(${columnIndex + 1}) {
            width: ${widthValue};
            min-width: ${widthValue};
            max-width: ${widthValue};
          }
        `;
      })
      .join('');
  };

  return (
    <TableWrapper customStyles={generateColumnStyles()}>
      <Table {...tableProps} />
    </TableWrapper>
  );
}

const TableWrapper = styled.div<{ customStyles: string }>`
  /* Force table to use fixed layout for precise column control */
  & table {
    table-layout: fixed;
  }

  /* Allow header text to wrap at word boundaries only */
  & table > thead > tr > th {
    white-space: normal;
    word-wrap: normal;
    overflow-wrap: normal;
    background-color: #f3f3f3;
  }

  /* Center content for all header cells except the first one */
  & table > thead > tr > th:not(:first-of-type) {
    text-align: center;
    vertical-align: middle;
  }

  /* Target the inner div within header cells */
  & table > thead > tr > th > div {
    white-space: normal;
    word-wrap: normal;
    overflow-wrap: normal;
  }

  /* Target the inner div within header cells (except first column) */
  & table > thead > tr > th:not(:first-of-type) > div {
    text-align: left;
    display: inline-block;
  }

  ${({ customStyles }) => customStyles}
`;
